import { ItemCreateRequest, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { chunk } from './ArrayHelper';
import { ItemExtrasAutoApplier } from './ItemExtrasAutoApplier';
import { SmartSendFieldsAreValid } from './SmartSendValidation';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';

export interface CreateItemReq {
  id: string;
  item: SmartSendItemApiModel;
  raw: string[];
}

export interface CreateItemRes {
  id: string;
  item?: SmartSendItemApiModel;
  raw: string[];
  error?: string;
}

const useCreateItemsHelper = () => {
  const { addNewItems, getItemsByIds } = useSmartSendApi();

  const createItemsHelper = async (
    items: CreateItemReq[],
    onSuccess: (items: SmartSendItemApiModel[], messages: string[]) => void,
    onFail: (items: string[], messages: string[]) => void,
    onComplete: (messages: string[]) => void
  ): Promise<{ success: SmartSendItemApiModel[]; failed: CreateItemRes[]; messages: string[] }> => {
    let successfulItems: SmartSendItemApiModel[] = [];
    const failedIds: CreateItemRes[] = items
      .filter((x) => !SmartSendFieldsAreValid(x.item))
      .map((x) => {
        return { ...x, error: 'Pre Validation' };
      });

    const messages: string[] = [];

    const promises = chunk(
      items.filter((x) => !failedIds.map((f) => f.id).includes(x.id)),
      SettingsCache.settings.Limits.CreateBatchSize
    ).map(async (x) => {
      // Convert to dictionary
      const dic: ItemCreateRequest = {};
      x.forEach((y) => {
        dic[y.id] = y.item;
      });

      // Let This Loop Success
      const thisLoop: string[] = []; // Id in db
      const thisLoopFail: string[] = []; // Client Id

      const add = await addNewItems(dic);
      if (add.success) {
        if (add.result.Message) {
          messages.push(add.result.Message);
        }
        for (const i in add.result.Items) {
          const itemCreateResponse = add.result.Items[i];
          if (itemCreateResponse.Success) {
            thisLoop.push(itemCreateResponse.Id);
          } else {
            const originalItemRequest = items.find((x) => x.id === i);
            failedIds.push({
              id: i,
              error: itemCreateResponse.Error,
              item: originalItemRequest?.item,
              raw: originalItemRequest?.raw
            });
            thisLoopFail.push(i);
          }
        }
      } else {
        x.forEach((element) => {
          failedIds.push(element);
        });
      }

      if (thisLoop.length) {
        const items = await getItemsByIds(thisLoop);
        if (items.success) {
          successfulItems = successfulItems.concat(items.result.Items);
          if (onSuccess) {
            onSuccess(items.result.Items.map(ItemExtrasAutoApplier), messages);
          }
        }
      }

      if (thisLoopFail.length) {
        if (onFail) {
          onFail(thisLoopFail, messages);
        }
      }
    });

    await Promise.all(promises);

    if (onComplete) {
      onComplete(messages);
    }

    return { success: successfulItems, failed: failedIds, messages: messages };
  };

  return { createItemsHelper };
};

export default useCreateItemsHelper;
